import React, { useCallback, useRef } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { SectionFavoriteCategoryDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import { TrackerExtraInfoProps, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useSlide } from 'ui-hooks/useSlide';
import { Style } from 'app-components/home/sections/RectBannerStyle';
import { hideScrollbar } from 'styles/common';
import { isPC } from 'constants/layout';
import { ModalHandler } from 'utils/ModalHandler';
import { ModalType } from 'constants/modal';
import { SectionFavoriteCategory } from 'app-components/home/sections/components/SectionFavoriteCategory';
import { useHomeSettingState } from 'contexts/HomeSettingProvider';
import { SectionSlideButton } from 'app-components/home/sections/components/SectionSlideButton';

interface SectionFavoriteCategoriesProps {
  section: SectionFavoriteCategoryDto;
}

const HorizontalGap = 8;

const SECTION_FAVORITE_CATEGORIES_STYLES = {
  titleContainer: 'flex flex-col justify-between items-start mb-4 gap-1 mx-[15px]',
  title: 'justify-start text-black text-sm font-bold',
  description: 'justify-start text-gray-500 text-xxxs font-normal',
};

export const SectionFavoriteCategories = ({
  section,
  trackerExtraInfo,
}: SectionFavoriteCategoriesProps & TrackerExtraInfoProps) => {
  const { tracker } = useTrackingState();
  const slideRef = useRef<HTMLDivElement>(null);
  const { handleSlide, showSlideButton, handleNextSlide, handlePrevSlide } = useSlide(slideRef, HorizontalGap);
  const { categoryIds: favoriteCategoryIds } = useHomeSettingState();

  const handleClick = useCallback(
    ({ title, categoryIds }: { title: string; categoryIds: number[] }) => {
      ModalHandler.show(ModalType.ConfirmFavoriteCategory, {
        title,
        categoryIds: categoryIds ?? [],
      });

      tracker.track(TrackingEventType.ClickSectionFavoriteCategory, {
        categoryIds: categoryIds ?? [],
        trackerExtraInfo,
      });
    },
    [tracker, trackerExtraInfo],
  );

  if (typeof window === 'undefined' || section.items.length === 0) {
    return null;
  }

  if (favoriteCategoryIds.length > 0) {
    return null;
  }

  return (
    <div>
      <div className={SECTION_FAVORITE_CATEGORIES_STYLES.titleContainer}>
        <div className={SECTION_FAVORITE_CATEGORIES_STYLES.title}>{section.title}</div>
        {section.sectionDescription && (
          <div className={SECTION_FAVORITE_CATEGORIES_STYLES.description}>{section.sectionDescription}</div>
        )}
      </div>
      <div className="relative">
        <SectionSlideButton
          showSlideButton={showSlideButton}
          handleNextSlide={handleNextSlide}
          handlePrevSlide={handlePrevSlide}
        />
        <StyledScrollContainer size={{ height: 70 }} ref={slideRef} onScroll={handleSlide}>
          {section.items.map(it => (
            <SectionFavoriteCategory key={it.id} item={it} onClick={() => handleClick(it)} />
          ))}
        </StyledScrollContainer>
      </div>
    </div>
  );
};

const StyledScrollContainer = styled.div(
  ({ size }: { size: Style }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    flex-flow: column wrap;
    align-content: flex-start;
    width: 100%;
    height: ${size.height}px;
    padding: 0 16px;
    gap: ${HorizontalGap}px;
    overflow-y: hidden;
    overflow-x: scroll;
    ${hideScrollbar};

    @media (${isPC}) {
      padding: 0;
      margin: 0 15px;
      overflow-x: hidden;
      width: calc(100% - 30px);
    }
  `,
);
